<template>
  <div class="app-pagination">
    <el-pagination
      :current-page="currentPage"
      :page-count="hasFixedPageSize ? undefined : pageCount"
      :page-size="globalPageSize"
      :page-sizes="availablePageSizes"
      :total="total"
      :background="background"
      :small="small"
      :disabled="disabled"
      :layout="effectiveLayout"
      @current-change="selectCurrentPage"
      @size-change="selectPageSize"
    />
    <el-popover
      v-if="!hasFixedPageSize"
      v-model:visible="customVisible"
      placement="top-end"
      :width="250"
      trigger="click"
      :disabled="disabled"
    >
      <template #reference>
        <el-button class="custom-page-size-button" link size="small" :disabled="disabled">
          {{ $t("Custom") }}
        </el-button>
      </template>
      <div class="custom-page-size-editor">
        <el-input-number
          v-model="customPageSize"
          :aria-label="$t('Custom')"
          :min="minPageSize"
          :max="maxPageSize"
          :step="1"
          step-strictly
          controls-position="right"
          size="small"
          :disabled="disabled"
          @keyup.enter="applyCustomPageSize"
        />
        <el-button type="primary" size="small" :disabled="disabled" @click="applyCustomPageSize">
          {{ $t("confirm") }}
        </el-button>
      </div>
    </el-popover>
  </div>
</template>

<script>
import {
  MAX_PAGE_SIZE,
  MIN_PAGE_SIZE,
  getPageSize,
  isValidPageSize,
  normalizePageSize,
  pageSizeOptions,
} from "@/utils/pageSize.mjs";

let paginationInstanceSequence = 0;

export default {
  name: "AppPagination",
  props: {
    currentPage: { type: Number, default: 1 },
    pageCount: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    layout: { type: String, default: "prev, pager, next" },
    background: { type: Boolean, default: true },
    small: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    fixedPageSize: {
      type: Number,
      default: null,
      validator: (value) => value === null || isValidPageSize(value),
    },
  },
  emits: ["current-change", "size-change", "update:currentPage"],
  data() {
    return {
      customVisible: false,
      customPageSize: isValidPageSize(this.fixedPageSize) ? this.fixedPageSize : getPageSize(),
      paginationInstanceId: `app-pagination-${++paginationInstanceSequence}`,
      suppressCurrentChange: false,
      minPageSize: MIN_PAGE_SIZE,
      maxPageSize: MAX_PAGE_SIZE,
    };
  },
  computed: {
    hasFixedPageSize() {
      return isValidPageSize(this.fixedPageSize);
    },
    globalPageSize() {
      return this.hasFixedPageSize ? this.fixedPageSize : this.$store.getters.pageSize;
    },
    availablePageSizes() {
      if (this.hasFixedPageSize) return [];
      return pageSizeOptions(this.globalPageSize);
    },
    effectiveLayout() {
      const items = String(this.layout || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      if (this.hasFixedPageSize) {
        return items.filter((item) => item !== "sizes").join(", ");
      }
      if (!items.includes("sizes")) {
        const totalIndex = items.indexOf("total");
        items.splice(totalIndex >= 0 ? totalIndex + 1 : 0, 0, "sizes");
      }
      return items.join(", ");
    },
    pageSizeChangeVersion() {
      if (this.hasFixedPageSize) return 0;
      return this.$store.state.pagination.changeVersion;
    },
    pageSizeChangeSourceId() {
      if (this.hasFixedPageSize) return null;
      return this.$store.state.pagination.changeSourceId;
    },
  },
  watch: {
    globalPageSize(value, previous) {
      this.customPageSize = normalizePageSize(value);
      if (value === previous) {
        return;
      }
      this.suppressCurrentChange = true;
      this.$nextTick(() => {
        this.suppressCurrentChange = false;
      });
    },
    pageSizeChangeVersion(value, previous) {
      if (this.disabled || this.hasFixedPageSize || value === previous || this.pageSizeChangeSourceId === this.paginationInstanceId) {
        return;
      }
      this.$emit("size-change", this.globalPageSize);
    },
  },
  methods: {
    selectPageSize(value) {
      if (this.disabled || this.hasFixedPageSize) return;
      const normalized = normalizePageSize(value);
      this.suppressCurrentChange = true;
      this.$store.dispatch("SetPageSize", {
        value: normalized,
        sourceId: this.paginationInstanceId,
      });
      this.$emit("size-change", normalized);
      this.$nextTick(() => {
        this.suppressCurrentChange = false;
      });
    },
    selectCurrentPage(value) {
      if (!this.disabled && !this.suppressCurrentChange) {
        this.$emit("update:currentPage", value);
        this.$emit("current-change", value);
      }
    },
    applyCustomPageSize() {
      if (this.disabled || this.hasFixedPageSize) return;
      if (!isValidPageSize(this.customPageSize)) {
        this.$message({
          showClose: true,
          type: "warning",
          message: `${this.$t("enterNumber")} (${MIN_PAGE_SIZE}-${MAX_PAGE_SIZE})`,
        });
        return;
      }
      this.selectPageSize(this.customPageSize);
      this.customVisible = false;
    },
  },
};
</script>

<style scoped>
.app-pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.custom-page-size-button {
  flex: 0 0 auto;
}

.custom-page-size-editor {
  display: flex;
  align-items: center;
  gap: 8px;
}

.custom-page-size-editor :deep(.el-input-number) {
  flex: 1 1 auto;
}
</style>
